import { AccessToken, AuthPluginError, GetTokenOptions } from '../types';
import { AuthPluginFacade } from '../facades/AuthPluginFacade';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { JWebErrorHandler } from '../../../services/JWeb';
import * as JWebTypes from '../../../services/JWeb/types';
import { getServices } from '../../../infra/startup';

class InitializationTokenCache {
  private static cachedToken: AccessToken | null = null;
  private static isAlive = true;

  public static async getToken(
    options?: GetTokenOptions
  ): Promise<AuthPluginError | AccessToken> {
    if (!this.isAlive) {
      return null;
    }

    if (this.cachedToken) {
      internalLogger?.debug?.d5?.({
        className: 'InitializationTokenCache',
        methodName: 'getToken()',
        message: 'Using cached token during initialization'
      });
      return this.cachedToken;
    }

    internalLogger?.debug?.d5?.({
      className: 'InitializationTokenCache',
      methodName: 'getToken()',
      message: 'First getToken call during initialization - caching result'
    });
    const result = await AuthPluginFacade.getToken(options);

    if (result && !(result as AuthPluginError).error) {
      this.cachedToken = result as AccessToken;
    }

    return result;
  }

  public static async rehydrateToken(): Promise<void> {
    if (!this.isAlive) {
      /* istanbul ignore next */
      internalLogger?.debug?.d4?.({
        className: 'InitializationTokenCache',
        methodName: 'rehydrateToken()',
        message:
          'Tried to rehydrate while TokenCache is dead. Skipping the rehydrate.',
        variables: {}
      });
      return null;
    }
    try {
      /* istanbul ignore next */
      internalLogger?.debug?.d4?.({
        className: 'InitializationTokenCache',
        methodName: 'rehydrateToken()',
        message: 'Initiating rehydrate of token cache.',
        variables: {}
      });
      // Rehydrate InitializationTokenCache which will be called by NativeAuthStrategy from RefreshWhenAuthStatusChanged.

      // Get JWeb token and compare with cached token
      const { tokenValue: cachedTokenValue } =
        await InitializationTokenCache.getToken().then((v) =>
          JWebErrorHandler<JWebTypes.AccessToken>(v)
        );
      const actualToken = await AuthPluginFacade.getToken().then((v) =>
        JWebErrorHandler<JWebTypes.AccessToken>(v)
      );

      // Only compare tokenValue if result is an AccessToken (not AuthPluginError)
      if (cachedTokenValue !== actualToken?.tokenValue) {
        /* istanbul ignore next */
        internalLogger?.debug?.d4?.({
          className: 'InitializationTokenCache',
          methodName: 'rehydrateToken()',
          message: 'Rehydrating token cache.',
          variables: {}
        });

        // Actual rehydrate
        this.cachedToken = actualToken;

        // During rehydrate, in case the received token is org-aware, we need to do a setTenant.
        if (actualToken.tenantId) {
          /* istanbul ignore next */
          internalLogger?.debug?.d4?.({
            className: 'InitializationTokenCache',
            methodName: 'rehydrateToken()',
            message: 'Rehydrate token: setting tenant.',
            variables: {}
          });
          const services = getServices();
          await services.tenantHandlerService.setTenant(
            actualToken.tenantId,
            services.tenantHandlerService.START_TENANT_LEVEL,
            {}
          );
        }
      }
    } catch (error) {
      /* istanbul ignore next */
      internalLogger?.error?.e3?.({
        className: 'InitializationTokenCache',
        methodName: 'rehydrateToken()',
        message: 'RehydrateToken failed.',
        error
      });
    }
  }

  public static killCache(): void {
    this.cachedToken = null;
    this.isAlive = false;
    internalLogger?.debug?.d5?.({
      className: 'InitializationTokenCache',
      methodName: 'killCache()',
      message: 'Token cache cleared after initialization'
    });
  }

  public static getCachedToken(): AccessToken {
    return this?.cachedToken;
  }
}

export default InitializationTokenCache;
